/**
 * @file   src\Pages\Unsubscribe.tsx
 * @brief  Unsubscribe page.
 * @date   APRIL, 2025
 * @author ZCO Engineer
 * @copyright (c) 2025, ZCO
 */

import { React, useState, useParams, useEffect, Button } from "../utils/thirdpartyComponents";
import CWInput from "src/components/CWInput";
import CWLogo from "../assets/images/CW-logo.svg";
import * as Yup from 'yup';
import { EMAIL_MAX_LENGTH } from "src/utils/constants";
import { useAppDispatch } from "src/hooks";
import { MessageToaster } from "src/utils/toastUtil";
import { getCompanySettings } from 'src/store/actions/settingsAction';
import { getFromLocalStorage } from 'src/utils/helper';
import { SubscriptionandwashpriceApi } from "src/utils/apiUrls";
import axios from '../config/axiosConfig';
import { UnsubscribeSchema } from "src/validations/UnsubscribeSchema";

const Unsubscribe = () => {
    // Route information variable
    const params = useParams();
    // Declare action dispatch.
    const dispatch = useAppDispatch();
    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    // Initialize states for formdata
    const [formData, setFormData] = useState({
        email: '',
    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        email: '',
    });

    // State to show modal
    const CarwashDataId = localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId')) : 0
    const [carWashId, setcarWashId] = useState(CarwashDataId);
    const [isCustomLogo, setCustomLogo] = useState(false);

    // Unsubscibe button handle function
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        
        try {
            await UnsubscribeSchema.validate(formData, { abortEarly: false });
            let carwashId = localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId')) : 0
            const requestData = {
                email: formData.email,
                carWashId: carwashId
            };
            const responseProfileUpdate = await axios.post(SubscriptionandwashpriceApi.UNSUBSCRIBE_API, requestData);
            if (responseProfileUpdate?.data?.error === 0) {
                //Clear errors
                setErrors({
                    email: '',
                });
                toaster.toastSuccess(responseProfileUpdate?.data?.message);
            }
            else {
                toaster.toastError(responseProfileUpdate?.data?.message);
            }

        } catch (error) {
            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                console.log(newErrors)
                setErrors(newErrors);
            }
        }
    }


    useEffect(() => {
        if (carWashId === null || !carWashId || carWashId === undefined) {
            const request = { carwashname: params.carwashname }
            fetchAppSettings(request);
        }
        localStorage.removeItem('CARWASH_COMPANY_DATA');
    }, [carWashId]);


    // Triggers whenever input is entered
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
        setFormData({
            ...formData,
            [name]: value,
        });

    }


    const settings = getFromLocalStorage("CARWASH_COMPANY_SETTINGS_DATA") || {};
    const IsCustom = settings?.IsCustom ? settings.IsCustom : 0;

    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    useEffect(() => {
        let logoFromStorage = IsCustom === 0 ? "" : getFromLocalStorage("LOGO_URL_SETTINGS_DATA");

        if (logoFromStorage && IsCustom !== 0) {
            const logoParts = logoFromStorage.split("/");
            if (logoParts.length > 1) {
                logoFromStorage = logoParts[0] + '/resized/160_' + logoParts[1];
                setCustomLogo(true)
            } else {
                setCustomLogo(false)
            }
            setLogoUrl(logoFromStorage);
        } else {
            setCustomLogo(false)
            setLogoUrl(CWLogo)
        }

    }, [settings]);

    useEffect(() => {
        if (logoUrl) {
            const link = document.createElement("link");
            link.rel = "preload";
            link.as = "image";
            link.href = (isCustomLogo) ? `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${logoUrl}` : CWLogo;
            document.head.appendChild(link);
            return () => {
                document.head.removeChild(link);
            };
        }
    }, [logoUrl, isCustomLogo]); // preloading


    const fetchAppSettings = async (request: any) => {
        await dispatch(getCompanySettings(request));
    }


    return (
        <div className="cw-container">
            <div className="cw-logo text-center">
                {getFromLocalStorage('carWashId') ?

                    logoUrl &&

                    <picture>
                        <img
                            src={isCustomLogo ? `${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${logoUrl}` : CWLogo}
                            alt="Car Wash Logo"
                            width={160}
                            height="auto"
                            loading="eager"
                            decoding="async"
                            fetchPriority="high"
                        />
                    </picture>
                    : ''}
            </div>
            <div className="signin-block">
                <h1>Unsubscribe</h1>
                <p className="sub-text">Please provide your registered email address for unsubscribing from the marketing emails.</p>
                <form onSubmit={(e) => { handleSubmit(e) }}>
                    <CWInput
                        id="email"
                        name="email"
                        type="text"
                        label="Email Address"
                        autoFocus={true}
                        maxLength={EMAIL_MAX_LENGTH}
                        value={formData.email}
                        errorMessage={errors.email}
                        onChange={handleInputChange}
                    />
                    <Button
                        type="submit"
                        variant="primary"
                        className="primary-btn btn-main"
                    >
                        Unsubscribe
                    </Button>
                </form>
            </div>
        </div>
    )
}

export default Unsubscribe;
